import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SystemSettings from '../components/admin/SystemSettings';
import VendorManagement from '../components/admin/VendorManagement';
import UserManagement from '../components/admin/UserManagement';
import RoutingRules from '../components/admin/RoutingRules';
import GLAccounts from '../components/admin/GLAccounts';
import JobManagement from '../components/admin/JobManagement';
import TierConfigPanel from '../components/admin/TierConfig';
import InvoiceSyncPanel from '../components/admin/InvoiceSyncPanel';
import ErrorLog from '../components/admin/ErrorLog';
import AIGLList from '../components/admin/AIGLList';

interface AdminPanelStyles {
  container: React.CSSProperties;
  header: React.CSSProperties;
  headerTop: React.CSSProperties;
  backLink: React.CSSProperties;
  title: React.CSSProperties;
  environment: React.CSSProperties;
  tabBar: React.CSSProperties;
  tab: React.CSSProperties;
  tabActive: React.CSSProperties;
  contentArea: React.CSSProperties;
  stackedSection: React.CSSProperties;
}

const ADMIN_TABS = [
  { id: 'settings', label: 'Settings' },
  { id: 'vendors', label: 'Vendors' },
  { id: 'users-routing', label: 'Users & Routing' },
  { id: 'gl-accounts', label: 'GL Accounts' },
  { id: 'jobs', label: 'Jobs' },
  { id: 'tier-config', label: 'Service Line' },
  { id: 'invoice-sync', label: 'Invoice Sync' },
  { id: 'ai-gl-list', label: 'AI GL List' },
  { id: 'error-log', label: 'Error Log' },
];

const AdminPanel: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>('settings');
  const [winteamEnv, setWinteamEnv] = useState<string>('TRAINING');

  const tabs = ADMIN_TABS;

  useEffect(() => {
    const hash = window.location.hash.replace('#', '') || 'settings';
    if (tabs.some(t => t.id === hash)) {
      setActiveTab(hash);
    } else {
      setActiveTab('settings');
    }
  }, [tabs]);

  useEffect(() => {
    window.location.hash = activeTab;
  }, [activeTab]);

  useEffect(() => {
    const fetchEnv = async () => {
      try {
        const { getSettings } = await import('../adminApi');
        const settings = await getSettings();
        const envSetting = settings.find(s => s.setting_key === 'active_winteam_environment');
        setWinteamEnv(envSetting?.setting_value?.toUpperCase() || 'TRAINING');
      } catch (error) {
        console.error('Failed to fetch WinTeam environment:', error);
      }
    };

    fetchEnv();
  }, []);

  const styles: AdminPanelStyles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#ffffff',
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      borderBottom: '1px solid #e0e0e0',
      backgroundColor: '#ffffff',
      padding: '20px 30px',
    },
    headerTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
    },
    backLink: {
      color: '#00386C',
      textDecoration: 'none',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'color 0.2s ease',
      background: 'none',
      border: 'none',
      padding: '0',
    },
    title: {
      fontSize: '28px',
      fontWeight: '600',
      color: '#00386C',
      margin: '0 auto',
    },
    environment: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '13px',
      fontWeight: '500',
      color: '#666666',
    },
    tabBar: {
      display: 'flex',
      gap: '0px',
      borderBottom: '1px solid #e0e0e0',
      backgroundColor: '#ffffff',
      paddingLeft: '30px',
    },
    tab: {
      padding: '16px 24px',
      fontSize: '14px',
      fontWeight: '500',
      color: '#666666',
      backgroundColor: 'transparent',
      border: 'none',
      cursor: 'pointer',
      position: 'relative',
      transition: 'color 0.2s ease',
    },
    tabActive: {
      color: '#00386C',
      borderBottom: '3px solid #00386C',
    },
    contentArea: {
      flex: '1',
      padding: '30px',
      overflowY: 'auto',
    },
    stackedSection: {
      marginBottom: '30px',
    },
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'settings':
        return <SystemSettings />;
      case 'vendors':
        return <VendorManagement />;
      case 'users-routing':
        return (
          <div style={styles.stackedSection}>
            <div style={{ marginBottom: '40px' }}>
              <UserManagement />
            </div>
            <div>
              <RoutingRules />
            </div>
          </div>
        );
      case 'gl-accounts':
        return <GLAccounts />;
      case 'jobs':
        return <JobManagement />;
      case 'tier-config':
        return <TierConfigPanel />;
      case 'invoice-sync':
        return <InvoiceSyncPanel />;
      case 'ai-gl-list':
        return <AIGLList />;
      case 'error-log':
        return <ErrorLog />;
      default:
        return <SystemSettings />;
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerTop}>
          <button
            onClick={() => navigate('/invoices')}
            style={styles.backLink}
          >
            ← Back to Invoices
          </button>
          <h1 style={styles.title}>Admin Panel</h1>
          <div style={styles.environment}>
            <span
              style={{
                display: 'inline-block',
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: winteamEnv === 'PRODUCTION' ? '#d32f2f' : '#4caf50',
              }}
            />
            WinTeam: <strong>{winteamEnv}</strong>
          </div>
        </div>
      </div>

      <div style={styles.tabBar}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              ...styles.tab,
              ...(activeTab === tab.id ? styles.tabActive : {}),
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div style={styles.contentArea}>{renderContent()}</div>
    </div>
  );
};

export default AdminPanel;
